/**
 * Génération et incrément du trigramme de laboratoire (cf. ticket #1456).
 *
 * Portage JavaScript de {@code TrigramGenerator} côté back, pour pouvoir
 * préremplir le champ côté UI avant la création. L'algo DOIT rester
 * synchronisé avec la version Java — toute modification ici doit être
 * reportée dans {@code TrigramGenerator.java}.
 *
 * Stratégie : initiales des mots significatifs (après filtrage des
 * stopwords FR/EN), complétées par les lettres suivantes du premier
 * mot significatif, puis par les lettres du premier mot du nom,
 * puis par 'X' (signal visuel pour correction via UI).
 *
 * Exemples (alignés sur le ticket) :
 *   "Laboratoire Interne" → "INT"
 *   "Eurofins"            → "EUR"
 *   "Lab externe Lyon"    → "LYO"
 *   "Lab 1"               → "LAB"
 */

const STOPWORDS = new Set([
    // FR
    'le', 'la', 'les', 'de', 'du', 'des', 'un', 'une', 'aux',
    // EN
    'the', 'of', 'and', 'a', 'an',
    // Génériques métier
    'labo', 'laboratoire', 'laboratories', 'laboratory',
    'lab', 'labs',
    // Adjectifs positionnels jugés non significatifs par le ticket
    'externe', 'external'
]);

const TRIGRAM_LENGTH = 3;
// Séparateurs = tout sauf lettres/chiffres Unicode (\p{L}\p{N}), comme le
// split Java. Un œ/Ø/Ł reste donc DANS son mot (ignoré plus tard par le
// filtre A-Z) au lieu de casser le mot en deux.
const SPLIT_REGEX = /[^\p{L}\p{N}]+/gu;

/**
 * Génère un trigramme candidat à partir du nom du laboratoire.
 *
 * @param {string} name nom du laboratoire
 * @returns {string|null} trigramme de 3 lettres A-Z, ou null si aucune lettre exploitable
 */
export function generateTrigram(name) {
    if (!name || !name.trim()) {
        return null;
    }
    const normalized = stripDiacritics(name).toUpperCase();
    const rawWords = normalized.split(SPLIT_REGEX);
    if (!rawWords.some(containsLetter)) {
        return null;
    }
    const significantWords = rawWords
        .filter(w => w.length > 0)
        .filter(containsLetter)
        .filter(w => !STOPWORDS.has(w.toLowerCase()));

    let trigram = '';

    if (significantWords.length > 0) {
        for (const word of significantWords) {
            if (trigram.length >= TRIGRAM_LENGTH) break;
            const first = firstLetter(word);
            if (first) trigram += first;
        }
        if (trigram.length < TRIGRAM_LENGTH) {
            const firstWord = significantWords[0];
            const firstIdx = indexOfFirstLetter(firstWord);
            trigram = appendRemainingLetters(trigram, firstWord, firstIdx + 1);
        }
    } else {
        for (const rawWord of rawWords) {
            if (!rawWord) continue;
            trigram = appendRemainingLetters(trigram, rawWord, 0);
            if (trigram.length >= TRIGRAM_LENGTH) break;
        }
    }

    while (trigram.length < TRIGRAM_LENGTH) {
        trigram += 'X';
    }
    return trigram.substring(0, TRIGRAM_LENGTH);
}

// ========== Helpers ==========

function stripDiacritics(input) {
    // NFKD comme le back (TrigramGenerator). Attention : NFKD ne décompose
    // PAS les ligatures (œ, æ) ni les lettres barrées (Ø, Ł, Đ) — elles sont
    // conservées comme lettres par le split puis ignorées par le filtre A-Z.
    return input.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
}

function firstLetter(word) {
    const idx = indexOfFirstLetter(word);
    return idx < 0 ? null : word[idx];
}

function containsLetter(word) {
    return indexOfFirstLetter(word) >= 0;
}

function indexOfFirstLetter(word) {
    for (let i = 0; i < word.length; i++) {
        if (/[A-Z]/.test(word[i])) return i;
    }
    return -1;
}

function appendRemainingLetters(trigram, word, startIndex) {
    let result = trigram;
    for (let i = startIndex; i < word.length && result.length < TRIGRAM_LENGTH; i++) {
        if (/[A-Z]/.test(word[i])) {
            result += word[i];
        }
    }
    return result;
}
